import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { usePartiesList } from '../../queries/parties';
import { PartyFormDrawer } from './PartyFormDrawer';
import { useProjectStore } from '../../project/project-store';
import type { PartyType } from '../../types/api';

const TYPE_OPTIONS: { value: PartyType | ''; label: string }[] = [
  { value: '', label: 'All types' },
  { value: 'individual', label: 'Individual' },
  { value: 'company', label: 'Company' },
  { value: 'trust', label: 'Trust' },
  { value: 'estate', label: 'Estate' },
  { value: 'government', label: 'Government' },
  { value: 'unknown', label: 'Unknown' },
];

export function PartiesListPage() {
  const navigate = useNavigate();
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';

  const [q, setQ] = useState('');
  const [type, setType] = useState<PartyType | ''>('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filters = useMemo(
    () => ({
      q: q.trim() || undefined,
      party_type: (type || undefined) as PartyType | undefined,
      limit: 200,
    }),
    [q, type],
  );

  const { data: parties = [], isLoading } = usePartiesList(filters);

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Parties</Title>
        <Text c="dimmed">Pick a project to view its parties.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Parties</Title>
          <Text c="dimmed" size="sm">
            Grantors, grantees, and other named entities. Search hits aliases too.
          </Text>
        </Stack>
        {canWrite && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setDrawerOpen(true)}>
            New party
          </Button>
        )}
      </Group>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search canonical name or alias"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={320}
        />
        <Select
          data={TYPE_OPTIONS}
          value={type}
          onChange={(v) => setType((v as PartyType) ?? '')}
          w={180}
          allowDeselect={false}
        />
      </Group>

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={parties}
        idAccessor="id"
        onRowClick={({ record }) => navigate(`/parties/${record.id}`)}
        rowStyle={() => ({ cursor: 'pointer' })}
        columns={[
          {
            accessor: 'canonical_name',
            title: 'Name',
            render: (p) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  <Link
                    to={`/parties/${p.id}`}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {p.canonical_name}
                  </Link>
                </Text>
                {p.notes && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {p.notes}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'party_type',
            title: 'Type',
            width: 120,
            render: (p) => (
              <Badge variant="light" tt="lowercase">
                {p.party_type}
              </Badge>
            ),
          },
          {
            accessor: 'alias_count',
            title: 'Aliases',
            width: 90,
            textAlign: 'right',
            render: (p) =>
              p.alias_count > 0 ? p.alias_count : <Text c="dimmed">—</Text>,
          },
          {
            accessor: 'mention_count',
            title: 'Mentions',
            width: 100,
            textAlign: 'right',
            render: (p) =>
              p.mention_count > 0 ? p.mention_count : <Text c="dimmed">—</Text>,
          },
        ]}
      />

      <PartyFormDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Stack>
  );
}
